import { Heart, Droplets, Calendar, MapPin, Activity, Building2, FlaskConical } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    donationId: string;
    donationType: string;
    bloodGroup: string;
    units: number;
    donationDate: string;
    venue: string;
    campName?: string;
    hemoglobin: string;
    bpReading: string;
    bloodBankName: string;
    bloodBankLicense?: string;
    componentsExtracted?: string[];
    nextEligibleDate?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

export default function BloodDonationCertificateTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Heart className="w-4 h-4 text-red-500" />
        <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
          Blood Donation Certificate
        </span>
      </div>

      {/* Thank You Message */}
      <div className="bg-red-50 border border-red-200 rounded px-3 py-2.5 text-center">
        <p className="text-[11px] font-semibold text-red-700">
          Thank you for your generous blood donation.
        </p>
        <p className="text-[10px] text-red-600 mt-0.5">
          Your contribution helps save lives and supports critical healthcare needs.
        </p>
      </div>

      {/* Donation Details Grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Donation ID:</span>
          <span className="text-gray-800 font-mono">{data.donationId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Donation Type:</span>
          <span className="text-gray-800">{data.donationType}</span>
        </div>
        <div className="flex items-center gap-2">
          <Droplets className="w-3 h-3 text-red-500" />
          <span className="font-semibold text-gray-600">Blood Group:</span>
          <span className="text-gray-800 font-bold">{data.bloodGroup}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Units Donated:</span>
          <span className="text-gray-800">{data.units}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-3 h-3 text-gray-500" />
          <span className="font-semibold text-gray-600">Date:</span>
          <span className="text-gray-800">{data.donationDate}</span>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-3 h-3 text-gray-500 mt-0.5 shrink-0" />
          <span className="font-semibold text-gray-600">Venue:</span>
          <span className="text-gray-800">{data.venue}</span>
        </div>
        {data.campName && (
          <div className="col-span-2 flex gap-2">
            <span className="font-semibold text-gray-600">Camp Name:</span>
            <span className="text-gray-800">{data.campName}</span>
          </div>
        )}
      </div>

      {/* Pre-Donation Vitals */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <Activity className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Pre-Donation Vitals
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[10px]">
          <div className="flex gap-2">
            <span className="font-semibold text-gray-600">Hemoglobin:</span>
            <span className="text-gray-800">{data.hemoglobin}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold text-gray-600">Blood Pressure:</span>
            <span className="text-gray-800">{data.bpReading}</span>
          </div>
        </div>
      </div>

      {/* Components Extracted */}
      {data.componentsExtracted && data.componentsExtracted.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <FlaskConical className="w-3 h-3 text-gray-600" />
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
              Components Extracted
            </h3>
          </div>
          <ul className="list-disc list-inside space-y-0.5 text-[10px] text-gray-700 bg-gray-50 px-3 py-2 border border-gray-200 rounded">
            {data.componentsExtracted.map((comp, i) => (
              <li key={i}>{comp}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Next Eligible Date */}
      {data.nextEligibleDate && (
        <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-[10px]">
          <div className="flex items-center gap-2">
            <Calendar className="w-3 h-3 text-blue-600" />
            <span className="font-semibold text-blue-700">Next Eligible Donation Date:</span>
            <span className="text-blue-800 font-medium">{data.nextEligibleDate}</span>
          </div>
        </div>
      )}

      {/* Blood Bank Info */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <div className="col-span-2 flex items-center gap-1.5 mb-1">
          <Building2 className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Blood Bank Information
          </h3>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Blood Bank:</span>
          <span className="text-gray-800">{data.bloodBankName}</span>
        </div>
        {data.bloodBankLicense && (
          <div className="flex gap-2">
            <span className="font-semibold text-gray-600">License No:</span>
            <span className="text-gray-800 font-mono">{data.bloodBankLicense}</span>
          </div>
        )}
      </div>

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
